<template>
  <div class="d-flex flex-column" style="width: 100%; max-width: 100%; overflow-x: hidden;">

    <v-sheet class="ma-1">
      <div class="d-flex flex-row flex-wrap align-center ga-2 mb-1">
        <v-autocomplete
          v-model="selectedQuarters"
          :items="quarters"
          label="Quarter"
          multiple
          clearable
          chips
          closable-chips
          variant="outlined"
          density="compact"
          style="min-width: 220px; max-width: 320px;"
          aria-label="Select quarters"
        />
        <v-autocomplete
          v-model="filterLocation"
          :items="availableLocations"
          label="Location"
          multiple
          clearable
          chips
          closable-chips
          variant="outlined"
          density="compact"
          style="min-width: 200px; max-width: 300px;"
          aria-label="Filter by location"
        />
        <v-autocomplete
          v-model="filterInOutRise"
          :items="availableInOutRise"
          label="IN/OUT RISE"
          multiple
          clearable
          chips
          closable-chips
          variant="outlined"
          density="compact"
          style="min-width: 170px; max-width: 240px;"
          aria-label="Filter by IN or OUT of RISE"
        />
        <v-autocomplete
          v-model="filterLanguage"
          :items="availableLanguages"
          label="Language"
          multiple
          clearable
          chips
          closable-chips
          variant="outlined"
          density="compact"
          style="min-width: 180px; max-width: 260px;"
          aria-label="Filter by language"
        />
        <v-autocomplete
          v-model="filterUserType"
          :items="availableUserTypes"
          label="User Type"
          multiple
          clearable
          chips
          closable-chips
          variant="outlined"
          density="compact"
          style="min-width: 170px; max-width: 240px;"
          aria-label="Filter by user type"
        />
      </div>

      <div class="d-flex flex-row flex-wrap align-center ga-2 mb-1">
        <v-btn
          color="primary"
          :disabled="selectedQuarters.length === 0 || loading"
          :loading="loading"
          aria-label="Build the send list for the selected quarters"
          tabindex="0"
          @click="getReportData"
          @keydown.enter="getReportData"
          @keydown.space.prevent="getReportData"
        >
          Submit
        </v-btn>
        <v-btn
          color="primary"
          variant="outlined"
          prepend-icon="mdi-download"
          :disabled="filteredItems.length === 0"
          aria-label="Download the filtered send list"
          tabindex="0"
          @click="downloadFile"
          @keydown.enter="downloadFile"
          @keydown.space.prevent="downloadFile"
        >
          Download
        </v-btn>
      </div>
    </v-sheet>

    <v-sheet class="ma-1">
      <v-card variant="outlined" title="Comms - Send List Builder" flat>
        <template v-slot:text>
          <v-text-field
            v-model="search"
            label="Filter"
            prepend-inner-icon="mdi-filter-outline"
            variant="outlined"
            single-line
            hide-details
            aria-label="Filter table results"
          />
        </template>

        <div v-if="loading" class="d-flex justify-center pa-8">
          <v-progress-circular indeterminate aria-label="Loading data" />
        </div>
        <div v-else-if="allItems.length === 0" class="pa-4">
          No data available. Select one or more quarters and click Submit.
        </div>
        <div v-else>
          <v-data-table
            :items-per-page="50"
            :items-per-page-options="[10, 25, 50, -1]"
            :headers="headers"
            :items="filteredItems"
            :search="search"
            fixed-header
            role="table"
            aria-label="Comms send list data"
            class="v-data-table"
          />
        </div>
      </v-card>
    </v-sheet>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useSystemStore } from '@/stores/system'
import { useExcelStore } from '@/stores/excel'

const systemStore = useSystemStore()
const excelStore = useExcelStore()

const quarters = ref([])
const selectedQuarters = ref([])

const allItems = ref([])
const search = ref('')
const loading = ref(false)

const filterLocation = ref([])
const filterInOutRise = ref([])
const filterLanguage = ref([])
const filterUserType = ref([])

const headers = ref([
  { title: 'Quarter', value: 'quarter', sortable: true, headerProps: { class: 'header-style' } },
  { title: 'Company Name', value: 'companyName', sortable: true, headerProps: { class: 'header-style' } },
  { title: 'First Name', value: 'firstName', sortable: true, headerProps: { class: 'header-style' } },
  { title: 'Affinity ID', value: 'affinityId', sortable: true, headerProps: { class: 'header-style' } },
  { title: 'Email', value: 'email', sortable: true, headerProps: { class: 'header-style' } },
  { title: 'Country', value: 'country', sortable: true, headerProps: { class: 'header-style' } },
  { title: 'Language', value: 'language', sortable: true, headerProps: { class: 'header-style' } },
  { title: 'IN/OUT RISE', value: 'inOutRISE', sortable: true, headerProps: { class: 'header-style' } },
  { title: 'User Type', value: 'userType', sortable: true, headerProps: { class: 'header-style' } },
])

// ── Filter option lists derived from the full data set ───────────────────────

const distinctSorted = (values) => [...new Set(values.filter(Boolean))].sort()

const availableLocations = computed(() => distinctSorted(allItems.value.map(i => i.country)))
const availableInOutRise = computed(() => distinctSorted(allItems.value.map(i => i.inOutRISE)))
const availableLanguages = computed(() => distinctSorted(allItems.value.map(i => i.language)))
const availableUserTypes = computed(() => distinctSorted(allItems.value.map(i => i.userType || i.type)))

// ── Filtered rows ────────────────────────────────────────────────────────────

const filteredItems = computed(() => {
  return allItems.value.filter(item => {
    if (filterLocation.value.length && !filterLocation.value.includes(item.country)) return false
    if (filterInOutRise.value.length && !filterInOutRise.value.includes(item.inOutRISE)) return false
    if (filterLanguage.value.length && !filterLanguage.value.includes(item.language)) return false
    const itemUserType = item.userType || item.type
    if (filterUserType.value.length && !filterUserType.value.includes(itemUserType)) return false
    return true
  })
})

// ── Data fetch ───────────────────────────────────────────────────────────────

const getReportData = () => {
  if (selectedQuarters.value.length === 0) {
    return
  }

  loading.value = true
  allItems.value = []

  systemStore.getCommsSendList(selectedQuarters.value).then(result => {
    allItems.value = result.responseData?.reportData || []
  }).catch(error => {
    console.log('comms send list builder, get data', error)
  }).finally(() => {
    loading.value = false
  })
}

const downloadFile = () => {
  if (filteredItems.value.length === 0) {
    return
  }

  const exportRows = filteredItems.value.map(item => ({
    Quarter: item.quarter,
    CompanyName: item.companyName,
    FirstName: item.firstName,
    AffinityId: item.affinityId,
    Email: item.email,
    Country: item.country,
    Language: item.language,
    InOutRISE: item.inOutRISE,
    UserType: item.userType || item.type,
  }))

  const now = new Date()
  const datestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
  const fileName = `CommsSendList_${datestamp}`

  excelStore.generateInMemoryExcelDocumentV2(exportRows, fileName).catch(error => {
    console.log('Generate In Memory Excel Document - CommsSendList', error)
  })
}

onMounted(() => {
  systemStore.getQuarters().then(result => {
    if (result.success) {
      quarters.value = result.quarters
        .filter(q => q.currentQuarters === true)
        .map(q => q.quarter)

      const currentQuarter = result.quarters.find(q => q.currentQuarter === true)?.quarter
      if (currentQuarter) {
        selectedQuarters.value = [currentQuarter]
      }
    }
  }).catch(error => {
    console.log('comms send list builder, get quarters', error)
  })
})
</script>

<style scoped>
:deep(.v-table__wrapper) {
  scrollbar-gutter: stable;
}

.header-style {
  background-color: #012a4b;
  color: white;
}

.v-data-table tbody tr:nth-child(even) {
  background-color: #e0e6ec;
  color: #202520;
}

.v-data-table tbody tr:nth-child(odd) {
  background-color: whitesmoke;
  color: #202520;
}
</style>
